import { viewport } from "@faire/slate/lib/mediaQuery";
import { MODAL_TITLE_ID } from "@faire/slate/private/modal/consts";
import { ModalHeaderCustomSlotFocus } from "@faire/slate/private/modal/ModalHeaderCustomSlotFocus";
import { ModalInlineHeader } from "@faire/slate/private/modal/ModalInlineHeader";
import { ModalStickyHeader } from "@faire/slate/private/modal/ModalStickyHeader";
import { ModalTitle } from "@faire/slate/private/modal/ModalTitle";
import {
  ModalHeaderConstraints,
  ModalHeaderVariant,
} from "@faire/slate/private/modal/types";
import React from "react";

type IProps = {
  /**
   * The children of the modal header.
   *
   * Available only when variant is customSlot.
   */
  children?: React.ReactNode;

  /**
   * The title of the modal header.
   *
   * This is required even for customSlot variant due to the scrolled state
   */
  title: string;

  /**
   * The variant of the modal header.
   *
   * @default "default"
   * @note "condensed" renders the header in its collapsed (compact) state immediately on mobile viewport, otherwise functionally the same as "default" for tablet / desktop
   */
  variant?: ModalHeaderVariant;
};

/**
 * Base modal header component.
 *
 * Title is always required, even when variant is customSlot.
 */
export const BaseModalHeader: React.FC<ModalHeaderConstraints<IProps>> = ({
  children,
  title,
  variant,
}) => {
  const isCustomSlot = variant === "customSlot";
  const isMobile = viewport.sm()?.matches;
  const alwaysCollapsed = isMobile && variant === "condensed";

  return (
    <>
      <ModalInlineHeader className={alwaysCollapsed ? "invisible" : undefined}>
        <>
          {!alwaysCollapsed ? <div className="h-9 md:h-0" /> : null}
          {isCustomSlot ? (
            <ModalHeaderCustomSlotFocus>{children}</ModalHeaderCustomSlotFocus>
          ) : (
            <ModalTitle truncate={alwaysCollapsed}>{title}</ModalTitle>
          )}
        </>
      </ModalInlineHeader>
      <ModalStickyHeader alwaysVisible={alwaysCollapsed}>
        <ModalTitle truncate id={MODAL_TITLE_ID}>
          {title}
        </ModalTitle>
      </ModalStickyHeader>
    </>
  );
};
